import React, { useState } from 'react';
import { AppBar, Tabs, Tab, Toolbar, Box, Button, Typography, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText, Divider } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { logout } from '../store/slices/authSlice';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { ref, get } from 'firebase/database';
import { database } from '../config/firebase';
import AutocallDepositManager from './AutocallDepositManager';
import { formatPhoneNumber } from '../utils/phoneNumberUtils';

interface DepositData {
  phonenumber: string;
  username: string;
  currentDeposit: number;
  lastUpdateDate: string;
}

interface DepositLog {
  timestamp: string;
  type: 'deposit' | 'withdraw';
  amount: number;
  date: string;
  reason?: string;
  balance?: number; // 해당 시점의 잔액
}

const MainTabs: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);

  // 예치금 관련 상태
  const [depositDialogOpen, setDepositDialogOpen] = useState(false);
  const [depositData, setDepositData] = useState<DepositData | null>(null);
  const [depositLogs, setDepositLogs] = useState<DepositLog[]>([]);

  // 오토콜 예치금 관리 관련 상태
  const [autocallDepositDialogOpen, setAutocallDepositDialogOpen] = useState(false);

  // 현재 페이지가 로그인 페이지인지 확인
  const isLoginPage = location.pathname === '/login';

  // 탭 인덱스 결정 (경로에 따라)
  const tabValue = location.pathname.startsWith('/drivers')
    ? 0
    : location.pathname.startsWith('/admin')
    ? 1
    : 0;

  const handleTabChange = (_: unknown, newValue: number) => {
    if (newValue === 0) navigate('/drivers');
    if (newValue === 1) navigate('/admin');
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const fetchDepositData = async () => {
    if (!user?.id) return;
    
    try {
      // deposit 테이블에서 데이터 가져오기
      const depositRef = ref(database, `deposit/${user.id}`);
      const depositSnapshot = await get(depositRef);
      
      if (depositSnapshot.exists()) {
        const data = depositSnapshot.val();
        setDepositData(data);
      } else {
        setDepositData({
          phonenumber: user.id,
          username: user.username,
          currentDeposit: 0,
          lastUpdateDate: ''
        });
      }

      // depositlogs 테이블에서 로그 가져오기
      const logsRef = ref(database, `depositlogs/${user.id}`);
      const logsSnapshot = await get(logsRef);
      
      if (logsSnapshot.exists()) {
        const logsData = logsSnapshot.val();
        const logs = Object.entries(logsData)
          .map(([timestamp, logData]: [string, any]) => ({
            timestamp,
            type: logData.type,
            amount: logData.amount,
            date: logData.date,
            reason: logData.reason || '',
            balance: logData.balance || 0
          }))
          .sort((a, b) => b.timestamp.localeCompare(a.timestamp));
        setDepositLogs(logs);
      } else {
        setDepositLogs([]);
      }
    } catch (error) {
      console.error('예치금 데이터 조회 실패:', error);
      setDepositData({
        phonenumber: user.id,
        username: user.username,
        currentDeposit: 0,
        lastUpdateDate: ''
      });
      setDepositLogs([]);
    }
  };

  const handleDepositClick = async () => {
    await fetchDepositData();
    setDepositDialogOpen(true);
  };

  const handleAutocallDepositClick = () => {
    setAutocallDepositDialogOpen(true);
  };

  const handleDepositDialogClose = () => {
    setDepositDialogOpen(false);
    setDepositData(null);
    setDepositLogs([]);
  };

  // 로그인 페이지에서는 AppBar를 표시하지 않음
  if (isLoginPage) {
    return null;
  }

  const showDepositButton = user?.grade === 'localmaster' && (user?.appname === 'smlink_M2_오토콜' || user?.appname === 'smlink_M2_이지캐치');
  const showAutocallLocalManagerDepositButton = user?.grade === 'localmanager' && (user?.appname === 'smlink_M2_오토콜' || user?.appname === 'smlink_M2_충청');

  return (
    <AppBar position="static" color="default" elevation={1}>
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={{ flexGrow: 1 }}>
          <Tabs value={tabValue} onChange={handleTabChange} textColor="primary" indicatorColor="primary">
            <Tab label="기사목록" />
            <Tab label="관리자" />
          </Tabs>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="subtitle1" component="span">
            {user?.username || '관리자'} 님
          </Typography>
          {showDepositButton && (
            <IconButton
              size="small"
              onClick={handleDepositClick}
              sx={{ color: 'success.main' }}
            >
              <AccountBalanceWalletIcon />
            </IconButton>
          )}
          {showAutocallLocalManagerDepositButton && (
            <IconButton
              size="small"
              onClick={handleAutocallDepositClick}
              sx={{ color: 'success.main' }}
            >
              <AccountBalanceWalletIcon />
            </IconButton>
          )}
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={handleLogout}
            size="small"
          >
            로그아웃
          </Button>
        </Box>
      </Toolbar>

      {/* 예치금 다이얼로그 */}
      <Dialog open={depositDialogOpen} onClose={handleDepositDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>예치금 정보</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              전화번호: {formatPhoneNumber(depositData?.phonenumber || '')}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              이름: {depositData?.username}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
              현재 예치금: {depositData?.currentDeposit?.toLocaleString() || '0'}원
            </Typography>
            {depositData?.lastUpdateDate && (
              <Typography variant="body2" color="text.secondary">
                마지막 업데이트: {depositData.lastUpdateDate}
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              예치금 히스토리
            </Typography>
            <Box sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
              {depositLogs.length > 0 ? (
                <List dense>
                  {depositLogs.map((log, index) => (
                    <ListItem key={index} divider>
                      <ListItemText
                        primary={
                          <Box>
                            {/* 모바일 뷰 - 세로 배치 */}
                            <Box sx={{ 
                              display: { xs: 'flex', md: 'none' }, 
                              flexDirection: 'column',
                              gap: 1
                            }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2">{log.date}</Typography>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: log.type === 'deposit' ? '#4caf50' : '#f44336',
                                    fontWeight: 600
                                  }}
                                >
                                  {log.type === 'deposit' ? '+' : '-'}{log.amount.toLocaleString()}원
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: log.type === 'deposit' ? '#4caf50' : '#f44336'
                                  }}
                                >
                                  {log.type === 'deposit' ? '입금' : '출금'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  잔액: {(log.balance ?? 0).toLocaleString()}원
                                </Typography>
                              </Box>
                            </Box>

                            {/* PC 뷰 - 가로 배치 */}
                            <Box sx={{ 
                              display: { xs: 'none', md: 'flex' }, 
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <Box sx={{ width: '140px' }}>{log.date}</Box>
                              <Box sx={{ 
                                width: '60px', 
                                textAlign: 'center', 
                                color: log.type === 'deposit' ? '#4caf50' : '#f44336'
                              }}>
                                {log.type === 'deposit' ? '입금' : '출금'}
                              </Box>
                              <Box sx={{ 
                                width: '120px', 
                                textAlign: 'right', 
                                fontWeight: 600,
                                color: log.type === 'deposit' ? '#4caf50' : '#f44336'
                              }}>
                                {log.type === 'deposit' ? '+' : '-'}{log.amount.toLocaleString()}원
                              </Box>
                              <Divider orientation="vertical" flexItem sx={{ mx: 2, display: { xs: 'none', md: 'block' } }} />
                              <Box sx={{ width: '120px', textAlign: 'right', color: 'text.secondary' }}>
                                잔액: {(log.balance ?? 0).toLocaleString()}원
                              </Box>
                            </Box>
                          </Box>
                        }
                        secondary={
                          log.type === 'withdraw' && log.reason ? (
                            <Box sx={{ mt: 0.5, color: 'text.secondary', fontSize: '0.875rem' }}>
                              사유: {log.reason}
                            </Box>
                          ) : null
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                  히스토리가 없습니다.
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDepositDialogClose}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* 오토콜 예치금 관리 다이얼로그 */}
      <AutocallDepositManager
        open={autocallDepositDialogOpen}
        onClose={() => setAutocallDepositDialogOpen(false)}
        selectedManager={{
          phonenumber: user?.id || '',
          username: user?.username || '',
          password: '',
          grade: user?.grade || '',
          appname: user?.appname || '',
          block: 'false',
          initdate: '',
          referralcode: user?.referralcode || ''
        }}
        currentUser={user}
      />
    </AppBar>
  );
};

export default MainTabs; 